import $ from 'jquery';
import {htmlEncode} from 'js-htmlencode';
import {memo, useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {getScrollableAncestors} from '../../utils';

const DEFAULT_SEARCH_URL = '/client/search-clients';
const DEFAULT_MIN_LENGTH = 3;
const DEFAULT_LIMIT = 25;
const TYPEAHEAD_INSTANCE_DATA_KEY = 'tt-typeahead';

export default memo(function ClientTypeahead({
    value,
    name,
    displayKey,
    searchParamName,
    relatedField,
    onChange,
    onSelectClient,
    suggestionsMenuOptions,
}) {
    const inputRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const onSelectClientRef = useRef(onSelectClient);

    useEffect(() => {
        onSelectClientRef.current = onSelectClient;
    }, [onSelectClient]);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    const handleChange = useCallback((event) => {
        onChangeRef.current?.({ target: { name, value: event.target.value } });
    }, [name]);

    useEffect(() => {
        const inputElement = inputRef.current;
        const $inputElement = $(inputElement);
        if (!inputElement || typeof window.Bloodhound === 'undefined' || !$.fn.typeahead || $inputElement.data(TYPEAHEAD_INSTANCE_DATA_KEY)) {
            return;
        }

        $inputElement.data('typeahead-menu-options', suggestionsMenuOptions ?? null);

        const engine = new window.Bloodhound({
            datumTokenizer: window.Bloodhound.tokenizers.whitespace,
            queryTokenizer: window.Bloodhound.tokenizers.whitespace,
            remote: {
                url: DEFAULT_SEARCH_URL,
                prepare: function (query, settings) {
                    const params = new URLSearchParams();
                    params.set(searchParamName, query);

                    if (relatedField && relatedField.param != null && relatedField.value != null) {
                        params.set(relatedField.param, relatedField.value);
                    }

                    settings.url = DEFAULT_SEARCH_URL + '?' + params.toString();

                    return settings;
                },
            },
            limit: DEFAULT_LIMIT,
        });
        engine.initialize();

        $inputElement.typeahead(
            { minLength: DEFAULT_MIN_LENGTH, scrollable: true },
            {
                display: displayKey,
                source: engine.ttAdapter(),
                templates: { suggestion: suggestionTemplate },
            }
        ).on('typeahead:asyncrequest', function () {
            $(this).removeClass('loading').addClass('loading');
        }).on('typeahead:asynccancel typeahead:asyncreceive', function () {
            $(this).removeClass('loading');
        }).on('typeahead:open', function () {
            positionTypeaheadMenuToBody(this);
        }).on('typeahead:close', function () {
            returnTypeaheadMenuToWrapper(this);
        });

        const handleSelect = (_, client) => {
            onSelectClientRef.current?.(client);
        };

        $inputElement.on('typeahead:select', handleSelect);

        return () => {
            $inputElement.off('typeahead:select', handleSelect);
            $inputElement.typeahead('destroy');
            inputElement.classList.remove('loading');
        };
    }, [displayKey, name, searchParamName, relatedField, suggestionsMenuOptions]);

    /**
     * Synchronize form value with the typeahead internal value.
     * This is needed when the same client was updated within the view in another input field
     * and then the same client is selected from suggestions menu.
     */
    useLayoutEffect(() => {
        const inputElement = inputRef.current;
        const $inputElement = $(inputElement);
        if (!inputElement || !$inputElement.data(TYPEAHEAD_INSTANCE_DATA_KEY)) {
            return;
        }

        const inputValue = value ?? '';
        if ($inputElement.typeahead('val') !== inputValue) {
            $inputElement.typeahead('val', inputValue);
        }
    }, [value]);

    return (
        <input
            ref={inputRef}
            type="text"
            name={name}
            value={value ?? ''}
            className="form-control input-sm"
            onChange={handleChange}
        />
    );
});

function suggestionTemplate(client) {
    const vipStatusLabel = client.vip_name ? '<div class="label" style="background-color: ' + client.vip_color_hex + '">' + htmlEncode(client.vip_name) + '</div>' : '';
    return '<div><div class="flex gap-1"><p><b>' + htmlEncode(client.client_lastname) + '</b> ' + htmlEncode(client.client_firstname) + '</p> ' + vipStatusLabel + '</div><small>' + htmlEncode(client.address) + '</small></div>';
}

function positionTypeaheadMenuToBody(inputElement) {
    const $wrapper = $(inputElement).closest('.twitter-typeahead');
    const $menu = $wrapper.find('.tt-menu');

    if (!$menu.length) {
        return;
    }

    const rect = inputElement.getBoundingClientRect();
    const menuOptions = $(inputElement).data('typeahead-menu-options');
    const width = menuOptions?.width ?? rect.width;
    const positionCss = {
        position: 'fixed',
        top: rect.bottom,
        left: menuOptions?.anchor === 'right' ? 'unset' : rect.left,
        right: menuOptions?.anchor === 'right' ? window.innerWidth - rect.right : 'unset',
        width: width,
        maxHeight: 200, // matches typeahead.less
        overflowX: 'auto', // original styles no longer target menu element when moved to body, so we need to reapply them
        zIndex: 95, // above modals but below page loader
    };

    $menu.data('typeahead-input', inputElement);
    $menu.appendTo(document.body).css(positionCss);

    const scrollTargets = [window, ...getScrollableAncestors(inputElement)];
    const closeHandler = function (event) {
        if (event.type === 'scroll' && $menu[0].contains(event.target)) {
            return;
        }
        closeOnScrollOrResize(inputElement);
    };
    $menu.data('typeahead-scroll-targets', scrollTargets);
    $menu.data('typeahead-close-handler', closeHandler);
    scrollTargets.forEach(function (target) {
        target.addEventListener('scroll', closeHandler, true);
    });
    window.addEventListener('resize', closeHandler);
}

function closeOnScrollOrResize(inputElement) {
    const $openMenu = $('body > .tt-menu').filter(function () { return $(this).data('typeahead-input') === inputElement; });
    if (!$openMenu.length) {
        return;
    }
    removeScrollAndResizeListeners($openMenu);
    inputElement.blur();
}

function returnTypeaheadMenuToWrapper(inputElement) {
    const $menu = $('body > .tt-menu').filter(function () { return $(this).data('typeahead-input') === inputElement; });
    if (!$menu.length) {
        return;
    }
    removeScrollAndResizeListeners($menu);
    const $wrapper = $(inputElement).closest('.twitter-typeahead');
    $menu.appendTo($wrapper);
    $menu.css({ position: '', top: '', left: 'unset', right: 'unset', width: '', zIndex: '' });
    $menu.removeData('typeahead-input');
}

function removeScrollAndResizeListeners($menu) {
    const scrollTargets = $menu.data('typeahead-scroll-targets');
    const handler = $menu.data('typeahead-close-handler');
    if (scrollTargets && handler) {
        scrollTargets.forEach(function (target) {
            target.removeEventListener('scroll', handler, true);
        });
        window.removeEventListener('resize', handler);
    }
    $menu.removeData('typeahead-scroll-targets').removeData('typeahead-close-handler');
}
